/**
 * Scroll-aware marketing navigation behavior.
 *
 * Responsibilities:
 * - Toggle `data-scrolled` on the `[data-nav]` header past a small scroll
 *   threshold so the CSS can switch from a translucent over-hero bar to a
 *   solid, condensed one.
 * - Mirror the desktop Solutions dropdown's open state onto `aria-expanded`
 *   for the CSS-focus dropdown (which has no native expanded state).
 * - Close the mobile `<details>` menu on link click, Escape, or outside click.
 *
 * Idempotent: `initNav` may run again on `phx:page-loading-stop`. Global
 * listeners are registered once via module-level flags; per-element wiring is
 * guarded with dataset flags so re-runs do not stack duplicate handlers.
 */

let scrollBound = false;
let outsideClickBound = false;

export function initNav(): void {
  const nav = document.querySelector<HTMLElement>('[data-nav]');
  if (nav === null) return;

  bindScrollState();
  bindDropdownAria(nav);
  bindMobileMenu(nav);
}

/** Reflect scroll position onto `[data-nav]`'s `data-scrolled` attribute. */
function bindScrollState(): void {
  const apply = (): void => {
    const nav = document.querySelector<HTMLElement>('[data-nav]');
    if (nav === null) return;
    nav.dataset['scrolled'] = window.scrollY > 24 ? 'true' : 'false';
  };

  apply();

  if (scrollBound) return;
  scrollBound = true;

  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        apply();
        ticking = false;
      });
    },
    { passive: true },
  );
}

/**
 * Keep `aria-expanded` in sync with the focus-driven Solutions dropdown and
 * make Escape close it while returning focus to the trigger.
 *
 * The panel's visibility is CSS `:focus-within`-driven, so focusing the
 * trigger after Escape would reopen it. Instead Escape force-hides the panel
 * with a `hidden` class that is cleared on the next open interaction
 * (trigger click / Enter / Space) or when focus leaves the dropdown.
 */
function bindDropdownAria(nav: HTMLElement): void {
  const trigger = nav.querySelector<HTMLElement>('[data-nav-dropdown]');
  if (trigger === null || trigger.dataset['ariaBound'] === 'true') return;

  const dropdown = trigger.closest<HTMLElement>('.dropdown');
  if (dropdown === null) return;
  const content = dropdown.querySelector<HTMLElement>('.dropdown-content');
  if (content === null) return;
  trigger.dataset['ariaBound'] = 'true';

  const set = (open: boolean): void => {
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };
  const reopen = (): void => {
    content.classList.remove('hidden');
    set(true);
  };

  dropdown.addEventListener('focusin', () => {
    if (!content.classList.contains('hidden')) set(true);
  });
  dropdown.addEventListener('focusout', (event: FocusEvent) => {
    const next = event.relatedTarget;
    if (next instanceof Node && dropdown.contains(next)) return;
    content.classList.remove('hidden');
    set(false);
  });
  trigger.addEventListener('click', reopen);
  trigger.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      reopen();
    }
  });
  dropdown.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    content.classList.add('hidden');
    trigger.focus();
    set(false);
  });
}

/** Close the mobile `<details>` menu on link click, Escape, or outside click. */
function bindMobileMenu(nav: HTMLElement): void {
  const menu = nav.querySelector<HTMLDetailsElement>('[data-nav-mobile]');
  if (menu === null) return;

  if (menu.dataset['bound'] !== 'true') {
    menu.dataset['bound'] = 'true';
    const summary = menu.querySelector<HTMLElement>('summary');

    menu.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
      link.addEventListener('click', () => {
        menu.open = false;
      });
    });

    // Return focus to the hamburger so keyboard users are not stranded
    // inside the collapsed panel.
    menu.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      menu.open = false;
      summary?.focus();
    });
  }

  if (outsideClickBound) return;
  outsideClickBound = true;

  document.addEventListener('click', (event: MouseEvent) => {
    const openMenu = document.querySelector<HTMLDetailsElement>('[data-nav-mobile]');
    if (openMenu === null) return;
    if (!openMenu.open) return;
    if (event.target instanceof Node && openMenu.contains(event.target)) return;
    openMenu.open = false;
  });
}
